import styled from 'styled-components';

import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { ReferralSource } from 'models/ReferralSource';
import { colors } from 'styles/theme/colors';
import EditIcon from 'svg/EditIcon';
import ThreeDotMenu from 'svg/ThreeDotMenu';
import TrashIcon from 'svg/TrashIcon';

export type ReferralSourceRowMenuProps = {
  referralSource: ReferralSource;
  // The admin surface gates on the admin_referral_source_* names, which is what OlioAdmin actually
  // holds in production (its role carries `admin_*`, so a customer-namespace name would not match).
  isAdmin?: boolean;
  onEdit: (referralSource: ReferralSource) => void;
  onDelete: (referralSource: ReferralSource) => void;
};

export default function ReferralSourceRowMenu({
  referralSource,
  isAdmin = false,
  onEdit,
  onDelete,
}: ReferralSourceRowMenuProps) {
  const { profile } = useProfile();

  const canEdit = profile.has(isAdmin ? PERMISSIONS.adminReferralSourceEdit : PERMISSIONS.referralSourceEdit);
  const canDelete = profile.has(isAdmin ? PERMISSIONS.adminReferralSourceDelete : PERMISSIONS.referralSourceDelete);

  // View-only users get no actions menu at all.
  if (!canEdit && !canDelete) return null;

  return (
    <Menu $size='small'>
      <Menu.Trigger>
        <KebabButton type='button' aria-label='Row actions'>
          <ThreeDotMenu />
        </KebabButton>
      </Menu.Trigger>
      <Menu.Content position='right'>
        {canEdit && (
          <Menu.Item onClick={() => onEdit(referralSource)} data-testid='edit-referral-source'>
            <EditIcon width={14} height={14} />
            Edit Referral Source
          </Menu.Item>
        )}
        {canEdit && canDelete && <Menu.Divider />}
        {canDelete && (
          <DeleteItem onClick={() => onDelete(referralSource)} data-testid='delete-referral-source'>
            <TrashIcon width={14} height={14} color={colors.red100} />
            Delete Referral Source
          </DeleteItem>
        )}
      </Menu.Content>
    </Menu>
  );
}

const KebabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const DeleteItem = styled(Menu.Item)`
  color: ${({ theme }) => theme.colors.red100};
`;
